import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { DemoUserSetupStep } from './demo-user-setup-step.enum';

export interface DemoUserSetupModalData {
  user: any;
}

@Component({
  selector: 'app-demo-user-setup-modal',
  templateUrl: './demo-user-setup-modal.component.html',
  styleUrls: ['./demo-user-setup-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoUserSetupModalComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();
  currentStep$ = new BehaviorSubject<DemoUserSetupStep>(
    DemoUserSetupStep.WELCOME,
  );
  DemoUserSetupStep = DemoUserSetupStep;

  userForm = new FormGroup({
    sex: new FormControl(null, [Validators.required]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,63}'),
    ]),
    phone: new FormControl('', [
      Validators.required,
      Validators.pattern('\\+?[1-9]\\d{0,14}'),
    ]),
  });

  removeValidationErrorEffect$ = this.userForm.valueChanges.pipe(
    takeUntil(this.destroyed$),
    filter(() => this.userForm.valid),
    tap(() => {
      this.showValidationError$.next(false);
      this.emailExistsError$.next(false);
    }),
  );

  showValidationError$ = new BehaviorSubject<boolean>(false);
  emailExistsError$ = new BehaviorSubject<boolean>(false);

  // Combined observable for template to check both error states
  validationState$ = combineLatest([
    this.showValidationError$,
    this.emailExistsError$,
  ]).pipe(
    map(([showValidationError, emailExistsError]) => ({
      showValidationError,
      emailExistsError,
    })),
  );

  constructor(
    public dialogRef: MatDialogRef<DemoUserSetupModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DemoUserSetupModalData,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private googleTagManagerService: GoogleTagManagerService,
  ) {
    this.removeValidationErrorEffect$.subscribe();
    this.googleTagManagerService.loadTagManager();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.currentStep$.complete();
  }

  nextStep(): void {
    if (this.currentStep$.value === DemoUserSetupStep.WELCOME) {
      this.currentStep$.next(DemoUserSetupStep.USER_FORM);
    }
  }

  trimPhone(): void {
    const phoneControl = this.userForm.get('phone');
    const phoneValue = phoneControl?.value;
    if (phoneValue) {
      phoneControl?.setValue(trimPhone(phoneValue));
    }
  }

  submitUserForm(): void {
    if (!this.userForm.valid) {
      this.userForm.markAsDirty();
      this.userForm.markAllAsTouched();
      this.showValidationError$.next(true);
      this.emailExistsError$.next(false);
      return;
    }

    // Hide validation errors if form is valid
    this.showValidationError$.next(false);
    this.emailExistsError$.next(false);

    // Get form data
    const formData = {
      sex: this.userForm.get('sex')?.value,
      firstName: this.userForm.get('firstName')?.value,
      lastName: this.userForm.get('lastName')?.value,
      email: this.userForm.get('email')?.value,
      phone: this.userForm.get('phone')?.value,
    };

    // Call the Meteor method to complete demo user setup
    Meteor.call('completeDemoUserSetup', formData, (error: Meteor.Error) => {
      if (error) {
        console.error('Error completing demo user setup:', error);
        // If email already exists, show specific error message
        if (error.error === 'email-already-exists') {
          this.showValidationError$.next(false);
          this.emailExistsError$.next(true);
          this.changeDetectorRef.detectChanges();
        } else {
          this.showValidationError$.next(true);
          this.emailExistsError$.next(false);
          this.changeDetectorRef.detectChanges();
        }
        return;
      }

      // Move to confirmation step on success
      this.currentStep$.next(DemoUserSetupStep.CONFIRM_EMAIL);
      this.changeDetectorRef.detectChanges();
    });
  }

  get email(): string {
    return this.userForm.get('email')?.value || '';
  }

  closeModal(): void {
    // Call cancelDemoSetup method before logging out
    Meteor.call('cancelDemoSetup', (error: Meteor.Error) => {
      if (error) {
        console.error('Error canceling demo setup:', error);
      }
      // Navigate to logout regardless of error
      this.router.navigate(['/logout']).then();
    });
  }
}
